/**
 * Where each agent input gets its value, keyed by the input's `key` (its path
 * in the agent's request). A source is `input.<column>`, `input` (the whole
 * item) or `const:<json>` (one value fixed for the experiment). Nothing is
 * mapped by default: an input the person did not map is not sent.
 */

import type { DatasetItem } from "@/features/datasets/types";
import type { PathInfo } from "@/features/evaluators/types";
import type { InputMapping } from "../types";

const CONST = "const:";
export const isFixed = (source: string | undefined) => !!source?.startsWith(CONST);
export const fixedSource = (value: string) => `${CONST}${JSON.stringify(value)}`;

/** A fixed source's value as text (`const:"medstar"` -> `medstar`, `const:12` -> `12`). */
export function fixedText(source: string): string {
  try {
    const value: unknown = JSON.parse(source.slice(CONST.length));
    return typeof value === "string" ? value : value === null ? "" : JSON.stringify(value);
  } catch {
    return source.slice(CONST.length);
  }
}

/** Top-level field names of the item input: from the first page of items and the dataset's paths. */
export function datasetFields(items: DatasetItem[], paths: PathInfo[]): string[] {
  const names = new Set<string>();
  for (const item of items) for (const key of Object.keys(item.input)) names.add(key);
  for (const p of paths) {
    const [root, field, ...rest] = p.path.split(".");
    if (root === "input" && field && rest.length === 0) names.add(field);
  }
  return [...names].sort();
}

/** Only inputs with a source go on the wire; nothing at all means "server default". */
export function inputMappingPayload(mapping: InputMapping): InputMapping | undefined {
  const set = Object.entries(mapping).filter(([, src]) => src.trim());
  return set.length ? Object.fromEntries(set) : undefined;
}

/** Parse the Advanced JSON: an object of strings, or an error message. */
export function parseMappingJson(text: string): InputMapping | string {
  try {
    const value: unknown = JSON.parse(text);
    if (typeof value !== "object" || value === null || Array.isArray(value)) return "Enter a JSON object.";
    const entries = Object.entries(value);
    if (entries.some(([, v]) => typeof v !== "string"))
      return 'Every value must be a source string, e.g. "input.question".';
    return Object.fromEntries(entries) as InputMapping;
  } catch {
    return "That is not valid JSON.";
  }
}
